/**
 * 요금제 데이터 관리 훅
 * 요금제, 구역, 운영시간 관리 상태를 담당
 */

import { useState, useCallback } from 'react';
import { pricingRepository } from '../repositories';
import { pricingService } from '../services';
import type {
  PricingPlan,
  SeatZone,
  ZonePricing,
  OperatingHours,
  PricingCreateRequest,
  PricingUpdateRequest,
} from '../models';

export const usePricingData = () => {
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [zones, setZones] = useState<SeatZone[]>([]);
  const [zonePricing, setZonePricing] = useState<ZonePricing[]>([]);
  const [operatingHours, setOperatingHours] = useState<OperatingHours[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * 요금제 목록 로드 (정렬 포함)
   */
  const loadPricingPlans = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      const plans = await pricingRepository.getPricingPlans();
      const sortedPlans = pricingService.sortPricingPlans(plans);
      setPricingPlans(sortedPlans);
    } catch (err) {
      console.error('요금제 로드 실패:', err);
      setError('요금제 정보를 불러오는데 실패했습니다.');
    }
  }, []);

  /**
   * 구역 목록 로드
   */
  const loadZones = useCallback(async (): Promise<void> => {
    try {
      const zoneList = await pricingRepository.getZones();
      setZones(zoneList);
    } catch (err) {
      console.error('구역 로드 실패:', err);
    }
  }, []);

  /**
   * 구역별 요금 로드
   */
  const loadZonePricing = useCallback(async (): Promise<void> => {
    try {
      const pricing = await pricingRepository.getZonePricing();
      setZonePricing(pricing);
    } catch (err) {
      console.error('구역별 요금 로드 실패:', err);
    }
  }, []);

  /**
   * 운영시간 로드
   */
  const loadOperatingHours = useCallback(async (): Promise<void> => {
    try {
      const hours = await pricingRepository.getOperatingHours();
      setOperatingHours(hours);
    } catch (err) {
      console.error('운영시간 로드 실패:', err);
    }
  }, []);

  /**
   * 모든 데이터 로드
   */
  const loadAllData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadPricingPlans(),
        loadZones(),
        loadZonePricing(),
        loadOperatingHours(),
      ]);
    } catch (err) {
      console.error('데이터 로드 실패:', err);
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [loadPricingPlans, loadZones, loadZonePricing, loadOperatingHours]);

  /**
   * 요금제 생성
   */
  const createPricingPlan = useCallback(
    async (data: PricingCreateRequest): Promise<boolean> => {
      // 유효성 검증
      const validation = pricingService.validatePricingPlan(data);
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      try {
        await pricingRepository.createPricingPlan(data);
        await loadPricingPlans();
        return true;
      } catch (err) {
        console.error('요금제 저장 실패:', err);
        alert('요금제 저장에 실패했습니다.');
        return false;
      }
    },
    [loadPricingPlans]
  );

  /**
   * 요금제 수정
   */
  const updatePricingPlan = useCallback(
    async (planId: string, data: PricingUpdateRequest): Promise<boolean> => {
      // 유효성 검증 (수정 시에는 기본 검증만 수행)
      if (!data.name || data.name.trim() === '') {
        alert('요금제 이름을 입력해주세요.');
        return false;
      }

      if (!(data.base_price >= 0)) {
        alert('가격은 0원 이상으로 입력해주세요.');
        return false;
      }

      try {
        await pricingRepository.updatePricingPlan(planId, data);
        await loadPricingPlans();
        return true;
      } catch (err) {
        console.error('요금제 수정 실패:', err);
        alert('요금제 수정에 실패했습니다.');
        return false;
      }
    },
    [loadPricingPlans]
  );

  /**
   * 요금제 삭제
   */
  const deletePricingPlan = useCallback(
    async (planId: string): Promise<boolean> => {
      if (!window.confirm('정말 이 요금제를 삭제하시겠습니까?')) {
        return false;
      }

      try {
        await pricingRepository.deletePricingPlan(planId);
        await loadPricingPlans();
        return true;
      } catch (err) {
        console.error('요금제 삭제 실패:', err);
        alert('요금제 삭제에 실패했습니다.');
        return false;
      }
    },
    [loadPricingPlans]
  );

  /**
   * 요금제 활성화 상태 토글
   */
  const togglePlanActive = useCallback(
    async (plan: PricingPlan): Promise<boolean> => {
      try {
        await pricingRepository.updatePricingPlan(plan.id, {
          name: plan.name,
          base_price: plan.base_price,
          description: plan.description,
          is_active: !plan.is_active,
        });
        await loadPricingPlans();
        return true;
      } catch (err) {
        console.error('요금제 상태 변경 실패:', err);
        alert('요금제 상태 변경에 실패했습니다.');
        return false;
      }
    },
    [loadPricingPlans]
  );

  /**
   * 운영시간 업데이트
   */
  const updateOperatingHours = useCallback(
    async (newHours: OperatingHours[]): Promise<boolean> => {
      // 유효성 검증
      for (const hour of newHours) {
        if (!hour.is_closed) {
          const validation = pricingService.validateOperatingHours({
            open_time: hour.open_time,
            close_time: hour.close_time,
          });
          if (!validation.valid) {
            alert(validation.message);
            return false;
          }
        }
      }

      try {
        const updated = await pricingRepository.updateOperatingHours(newHours);
        setOperatingHours(updated);
        return true;
      } catch (err) {
        console.error('운영시간 업데이트 실패:', err);
        alert('운영시간 업데이트에 실패했습니다.');
        return false;
      }
    },
    []
  );

  return {
    // 상태
    pricingPlans,
    zones,
    zonePricing,
    operatingHours,
    loading,
    error,

    // 함수
    loadAllData,
    loadPricingPlans,
    loadZones,
    loadZonePricing,
    loadOperatingHours,
    createPricingPlan,
    updatePricingPlan,
    deletePricingPlan,
    togglePlanActive,
    updateOperatingHours,
  };
};
